// Shared content for Build Type dropdown + cross-sell popover.
// Used by both BuildTypeField.jsx (right column — legacy) and
// BuildTypeControls.jsx (top header — new). Single source of truth so
// the sales lead only edits copy once.

export const BUILD_TYPES = [
  { key: "new_build_house",      label: "New Build House" },
  { key: "flat_apartment_hmo",   label: "Flat / Apartment / HMO" },
  { key: "loft_conversion",      label: "Loft Conversion" },
  { key: "house_refurb",         label: "House Refurb / Renovation" },
  { key: "commercial_new_build", label: "Commercial New Build / Fit-Out" },
  { key: "extension",            label: "Single / Double Storey Extension" },
  { key: "other",                label: "Other · Detail" },
];

export const CROSS_SELL_MAP = {
  new_build_house: {
    upsells: [
      "Full acoustic insulation upgrade (Hush Panel 30 as premium tier)",
      "Underfloor heating substrate — often forgotten at spec stage",
      "Fire-rated boards for garage-to-house adjoining walls",
      "Weather-membrane + tape package (roll into carriage-inclusive)",
    ],
    buzzwords: ["Part E acoustic compliance", "U-value target", "BREEAM points", "Cavity closer", "Vapour control layer"],
    scripts: [
      "\"Are you specced for Part E acoustic compliance across the party walls? Our Hush Panel 30 spec ticks that.\"",
      "\"On new builds we usually see the underfloor heating substrate get ordered separately — can we bundle that into this quote?\"",
      "\"If the site's targeting BREEAM, our full membrane + insulation package earns points on materials and waste.\"",
    ],
  },
  flat_apartment_hmo: {
    upsells: [
      "Acoustic underlay (Part E is non-negotiable for HMOs)",
      "Fire-rated plasterboard on ceilings + partitions",
      "Silent floor system — big premium for HMO landlords",
      "Communal-area soundproofing (stairwells, common corridors)",
    ],
    buzzwords: ["Part E", "Part B fire", "HMO licencing", "Impact sound", "Airborne sound", "Silent Floor"],
    scripts: [
      "\"HMO acoustic testing is expensive to fail — our Hush spec pre-tests to 45dB airborne. Worth the 8% price uplift.\"",
      "\"For fire compliance in the communal areas, are you specced with Type F pink board or standard?\"",
      "\"Landlords love the Silent Floor package — tenants stop complaining and voids drop. Want me to add pricing?\"",
    ],
  },
  loft_conversion: {
    upsells: [
      "Party wall acoustic upgrade (below the new loft floor)",
      "Roof insulation between + under rafters (double-treatment)",
      "Fire-rated door + frame (Building Regs approval)",
      "Fire-rated boards on the new stair partition",
    ],
    buzzwords: ["Between-rafter + Below-rafter", "Warm roof / Cold roof", "Trussed rafter", "PIR board", "Party wall notice"],
    scripts: [
      "\"On lofts we always ask: is this a warm roof or cold roof spec? Between + below rafter treatment gets you higher U-values with less depth.\"",
      "\"Fire regs require FD30 doors on the new landing — I can add a pre-glazed frame kit with the boards package.\"",
      "\"Have the neighbours had the Party Wall notice? Our acoustic strip on the shared wall keeps it silent under Part E.\"",
    ],
  },
  house_refurb: {
    upsells: [
      "Full wall + ceiling acoustic re-treatment (Hush over existing plasterboard)",
      "Damp-proof course + membrane rescue for basements",
      "Underfloor heating substrate + acoustic underlay combo",
      "Timber battens + PIR insulation for cold external walls",
    ],
    buzzwords: ["Internal wall insulation", "IWI", "Thermal bridging", "Damp-proof course", "Retrofit"],
    scripts: [
      "\"Refurbs are the best time to fix cold external walls — IWI boards over the render are a 20-year fix for £5/m².\"",
      "\"Are you re-doing floors? Underfloor heating substrate + acoustic underlay is a two-in-one bundle we do a lot of.\"",
      "\"Any damp issues below-grade? Our tanking + membrane kit works out cheaper than remedial DPC.\"",
    ],
  },
  commercial_new_build: {
    upsells: [
      "Class A fire boards for stair cores + escape routes",
      "Commercial-grade acoustic system (BB93 for schools, HTM for hospitals)",
      "Full-height partition system with STC 55+ rating",
      "Suspended-ceiling tiles + grid — often specced separately",
    ],
    buzzwords: ["BB93", "HTM 08-01", "Class 0", "STC rating", "Fit-out", "Category A / Category B"],
    scripts: [
      "\"On schools we work to BB93 spec — 40dB DnT,w minimum. Can I confirm you're specced to that grade?\"",
      "\"For a Cat B fit-out our partitions ship pre-taped and jointed — saves your subbies 3 days on site.\"",
      "\"Class 0 fire rating is non-negotiable in escape corridors — our default board is Class B certified. Want me to swap to Class 0 across the board?\"",
    ],
  },
  extension: {
    upsells: [
      "Blockwork + insulation cavity kit (bundle discount vs separate)",
      "Party wall acoustic upgrade (semi-detached extensions)",
      "Bi-fold door lintels + acoustic packers",
      "Roof insulation between + below rafters",
    ],
    buzzwords: ["Full-fill cavity", "Wall tie", "Cavity closer", "Trickle vent", "Steel beam pack"],
    scripts: [
      "\"On extensions we always bundle full-fill cavity insulation + wall ties + cavity closers as one package — you'll never have a merchant hold you up mid-build.\"",
      "\"Semi-detached extensions: we treat the party wall with a Hush strip — Part E compliance and neighbour goodwill in one.\"",
      "\"Bi-fold going in? We stock the lintel with pre-fitted acoustic packers — worth 30 minutes on the ground.\"",
    ],
  },
  other: {
    upsells: [
      "Ask about upcoming phases — repeat orders unlock 5% loyalty rebate",
      "Trade-account signup for VIP pricing (Bronze → Silver in 3 orders)",
      "Carriage-inclusive quote (Central London next-day)",
    ],
    buzzwords: ["Loyalty rebate", "Trade account", "VIP tier", "Carriage-inclusive"],
    scripts: [
      "\"Even if this doesn't fit our standard packs, we can price it up — mind sharing the drawings or takeoff?\"",
      "\"If there's more coming on this project, opening a trade account gets you VIP pricing from order 3 onwards.\"",
    ],
  },
};

// Vehicle & Weight guide — used by the Site & Delivery panel.
export const WEIGHT_BANDS = [
  { max_kg: 500,
    weight_label: "Under 500kg",
    vehicle: "Transit van / Sprinter",
    notes: "Standard kerbside drop. No forklift needed.",
    forklift_needed: false },
  { max_kg: 1000,
    weight_label: "500kg–1,000kg",
    vehicle: "Luton / Dropside",
    notes: "Team handball usually fine. Confirm access.",
    forklift_needed: false },
  { max_kg: 10000,
    weight_label: "Over 1,000kg",
    vehicle: "Lorry with Moffat HIAB",
    notes: "Forks or handball team must be ready. Brief customer before delivery day.",
    forklift_needed: true },
  { max_kg: Infinity,
    weight_label: "Abnormal / bulky",
    vehicle: "Varies — check with Galaxy",
    notes: "Long boards, full PIR pallets, large timber packs. Always flag to Galaxy in PO notes.",
    forklift_needed: true },
];

export function suggestVehicle(weightKg) {
  const kg = Number(weightKg) || 0;
  if (kg <= 0) return null;
  return WEIGHT_BANDS.find((b) => kg <= b.max_kg) || WEIGHT_BANDS[WEIGHT_BANDS.length - 1];
}
